import React from 'react';
import { VoucherHotel } from 'services/ItineraryManagerApi/types';
import { VoucherAccommodationEdit } from './VoucherAccommodationEdit';
import { SlideToggle } from 'ui/SlideToggle';
import { sortHotelsByEarliestDate } from '../../utils';

interface IVoucherHotelEditProps {
  hotels: VoucherHotel[];
  voucherUuid: string;
  onHotelPhoneVisibilityToggle: (hotelUuid: string, isVisible: boolean) => void;
  onHotelEmailVisibilityToggle: (hotelUuid: string, isVisible: boolean) => void;
  onAccommodationVisibilityToggle: (hotelUuid: string, accommodationUuid: string, isVisible: boolean) => void;
  onAccommodationDescriptionChange: (hotelUuid: string, accommodationUuid: string, description: string) => void;
  onAccommodationMoreTextChange: (hotelUuid: string, accommodationUuid: string, moreText: string) => void;
  onMealPlanDescriptionChange: (
    hotelUuid: string,
    accommodationUuid: string,
    mealPlanUuid: string,
    description: string
  ) => void;
  onSupplementVisibilityToggle: (
    hotelUuid: string,
    accommodationUuid: string,
    supplementUuid: string,
    isVisible: boolean
  ) => void;
  onSupplementTertiaryTextChange: (
    hotelUuid: string,
    accommodationUuid: string,
    supplementUuid: string,
    tertiaryText: string
  ) => void;
  onAdditionalServiceVisibilityToggle: (
    hotelUuid: string,
    accommodationUuid: string,
    serviceUuid: string,
    isVisible: boolean
  ) => void;
  onAdditionalServiceTertiaryTextChange: (
    hotelUuid: string,
    accommodationUuid: string,
    serviceUuid: string,
    tertiaryText: string
  ) => void;
  onSupplementsMoreInfoChange: (hotelUuid: string, accommodationUuid: string, text: string) => void;
  onAdditionalServicesMoreInfoChange: (hotelUuid: string, accommodationUuid: string, text: string) => void;
  onRateFeaturesVisibilityToggle: (hotelUuid: string, accommodationUuid: string, isVisible: boolean) => void;
  onAccommodationAddUpload: (hotelUuid: string, accommodationUuid: string, uploadData: any) => void;
  onAccommodationRemoveUpload: (hotelUuid: string, accommodationUuid: string, uploadIndex: number) => void;
  onMealPlanAddUpload: (hotelUuid: string, accommodationUuid: string, mealPlanUuid: string, uploadData: any) => void;
  onMealPlanRemoveUpload: (hotelUuid: string, accommodationUuid: string, mealPlanUuid: string, uploadIndex: number) => void;
}

export const VoucherHotelEdit: React.FC<IVoucherHotelEditProps> = ({
  hotels,
  voucherUuid,
  onHotelPhoneVisibilityToggle,
  onHotelEmailVisibilityToggle,
  onAccommodationVisibilityToggle,
  onAccommodationDescriptionChange,
  onAccommodationMoreTextChange,
  onMealPlanDescriptionChange,
  onSupplementVisibilityToggle,
  onSupplementTertiaryTextChange,
  onAdditionalServiceVisibilityToggle,
  onAdditionalServiceTertiaryTextChange,
  onSupplementsMoreInfoChange,
  onAdditionalServicesMoreInfoChange,
  onRateFeaturesVisibilityToggle,
  onAccommodationAddUpload,
  onAccommodationRemoveUpload,
  onMealPlanAddUpload,
  onMealPlanRemoveUpload,
}) => {
  if (hotels.length === 0) return null;

  return (
    <div className="mb-8">
      {sortHotelsByEarliestDate(hotels).map(hotel => (
        <div key={hotel.uuid}>
          <span className="font-pt-sans block text-17px text-black font-bold mb-10px">{hotel.name}</span>
          
          {/* Hotel Contact Information Section */}
          <div className="mb-4">
            {/* Phone Section */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1">
                <span className="font-pt-sans text-15px text-black">+{hotel.phoneNumber}</span>
              </div>
              <SlideToggle
                id={`hotel-phone-${hotel.uuid}`}
                checked={hotel.isPhoneVisible !== false}
                onActivate={() => onHotelPhoneVisibilityToggle(hotel.uuid, true)}
                onDeactivate={() => onHotelPhoneVisibilityToggle(hotel.uuid, false)}
                labelClassName={hotel.isPhoneVisible === false ? 'bg-gray-20' : 'bg-teal-40'}
                buttonClassName={hotel.isPhoneVisible === false ? 'bg-gray-100' : 'bg-teal-100'}
              />
            </div>
            
            {/* Email Section */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <span className="font-pt-sans text-15px text-black">{hotel.email}</span>
              </div>
              <SlideToggle
                id={`hotel-email-${hotel.uuid}`}
                checked={hotel.isEmailVisible !== false}
                onActivate={() => onHotelEmailVisibilityToggle(hotel.uuid, true)}
                onDeactivate={() => onHotelEmailVisibilityToggle(hotel.uuid, false)}
                labelClassName={hotel.isEmailVisible === false ? 'bg-gray-20' : 'bg-teal-40'}
                buttonClassName={hotel.isEmailVisible === false ? 'bg-gray-100' : 'bg-teal-100'}
              />
            </div>
          </div>

          <div>
            {/* Render each accommodation as a detail component */}
            {hotel.accommodations.map(accommodation => (
              <VoucherAccommodationEdit
                key={accommodation.uuid}
                hotel={hotel}
                accommodation={accommodation}
                voucherUuid={voucherUuid}
                onVisibilityToggle={onAccommodationVisibilityToggle}
                onDescriptionChange={onAccommodationDescriptionChange}
                onMoreTextChange={onAccommodationMoreTextChange}
                onMealPlanDescriptionChange={onMealPlanDescriptionChange}
                onSupplementVisibilityToggle={onSupplementVisibilityToggle}
                onSupplementTertiaryTextChange={onSupplementTertiaryTextChange}
                onAdditionalServiceVisibilityToggle={onAdditionalServiceVisibilityToggle}
                onAdditionalServiceTertiaryTextChange={onAdditionalServiceTertiaryTextChange}
                onSupplementsMoreInfoChange={onSupplementsMoreInfoChange}
                onAdditionalServicesMoreInfoChange={onAdditionalServicesMoreInfoChange}
                onRateFeaturesVisibilityToggle={onRateFeaturesVisibilityToggle}
                onAccommodationAddUpload={onAccommodationAddUpload}
                onAccommodationRemoveUpload={onAccommodationRemoveUpload}
                onMealPlanAddUpload={onMealPlanAddUpload}
                onMealPlanRemoveUpload={onMealPlanRemoveUpload}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
